/**
 * AssociatedRecordsField — render condiviso del picker "Associazioni".
 *
 * Tre modalità di display:
 *  - `always-open`: Autocomplete sempre visibile (FormModal create).
 *  - `click-to-edit`: trigger "Associazioni · N" che apre un Popover col
 *     pannello a due colonne.
 *  - `panel`: solo il pannello a due colonne, inline (nessun trigger, nessun
 *     popover). Usato da ActivityTools, che fornisce il bottone esterno.
 *
 * Pannello a due colonne (sinistra: tipologie con conteggio dei selezionati;
 * destra: ricerca + risultati raggruppati per tipo, coi già selezionati).
 *
 * Prop driven da `useAssociatedRecordsBinder` (vedi pickerProps spread).
 */

import { useEffect, useMemo, useState, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { entityTypeLabelPlural, getEntityType } from '../entityRegistry/entityRegistry';
import type { AssociatedRecord } from './useAssociatedRecordsPicker';
import type { useAssociatedRecordsPicker } from './useAssociatedRecordsPicker';

const sameRecord = (a: AssociatedRecord, b: AssociatedRecord) =>
  a.typeName === b.typeName && a.id === b.id;

const recordKey = (r: AssociatedRecord) => `${r.typeName}:${r.id}`;

/** Etichetta plurale visualizzata per un type, tradotta (dal registry; fallback al typeName). */
function typeLabel(typeName: string, t: (key: string) => string): string {
  return entityTypeLabelPlural(getEntityType(typeName), t, typeName);
}

/** Props comuni del pannello (sia popover sia inline). */
export interface AssociationsPanelProps {
  records: AssociatedRecord[];
  recordsAutocompleteProps: ReturnType<typeof useAssociatedRecordsPicker>['autocompleteProps'];
  recordsListboxProps: ReturnType<typeof useAssociatedRecordsPicker>['listboxProps'];
  recordsLoading: boolean;
  recordsSaving?: boolean;
  onChange: (records: AssociatedRecord[]) => void | Promise<void>;
  associableTypeNames?: readonly string[];
  /**
   * Notifica al picker il tipo attivo per restringere la ricerca server-side
   * (`<typeName>` cerca solo su quel tipo, `null` = vista "Selezionate", nessuna
   * ricerca). Opzionale: i display mode senza filtro per tipo non la usano.
   */
  onActiveTypeChange?: (typeName: string | null) => void;
}

interface AssociatedRecordsFieldProps extends AssociationsPanelProps {
  /** ricevuto via spread ma non usato direttamente (le opzioni arrivano da recordsAutocompleteProps). */
  recordOptions?: AssociatedRecord[];
  displayMode: 'always-open' | 'click-to-edit' | 'panel';
  className?: string;
}

export function AssociatedRecordsField({
  records,
  recordsAutocompleteProps,
  recordsListboxProps,
  recordsLoading,
  recordsSaving = false,
  onChange,
  displayMode,
  className,
  associableTypeNames = [],
  onActiveTypeChange,
}: AssociatedRecordsFieldProps) {
  const { t } = useTranslation();

  const placeholder =
    records.length === 0
      ? t('common.associateRecordsEmpty')
      : t('common.associateRecordsPlaceholder', { count: records.length });

  // ── always-open: una sola Autocomplete sempre montata ────────────────
  if (displayMode === 'always-open') {
    return (
      <Autocomplete
        {...recordsAutocompleteProps}
        multiple
        value={records}
        onChange={(_, val) => onChange(val)}
        groupBy={(o) => o.typeName}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.typeName === b.typeName && a.id === b.id}
        loading={recordsLoading}
        slotProps={{ listbox: recordsListboxProps }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('common.associateRecords')}
            placeholder={placeholder}
          />
        )}
        className={className ?? 'form-field'}
        size="small"
      />
    );
  }

  // ── panel: solo il pannello inline (no trigger, no popover) ───────────
  if (displayMode === 'panel') {
    return (
      <AssociationsPanelInner
        inline
        records={records}
        recordsAutocompleteProps={recordsAutocompleteProps}
        recordsListboxProps={recordsListboxProps}
        recordsLoading={recordsLoading}
        recordsSaving={recordsSaving}
        onChange={onChange}
        associableTypeNames={associableTypeNames}
        onActiveTypeChange={onActiveTypeChange}
      />
    );
  }

  // ── click-to-edit: trigger "Associazioni · N" + popover ───────────────
  return (
    <AssociationsPopoverField
      records={records}
      recordsAutocompleteProps={recordsAutocompleteProps}
      recordsListboxProps={recordsListboxProps}
      recordsLoading={recordsLoading}
      recordsSaving={recordsSaving}
      onChange={onChange}
      className={className}
      associableTypeNames={associableTypeNames}
      onActiveTypeChange={onActiveTypeChange}
    />
  );
}

// Trigger compatto + Popover che ospita il pannello.
function AssociationsPopoverField({
  className,
  ...panelProps
}: AssociationsPanelProps & { className?: string }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return (
    <div className={className ?? 'field-row'}>
      <button
        type="button"
        className="associations-trigger"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={panelProps.recordsSaving}
      >
        <LinkIcon fontSize="small" />
        <span>{t('common.associations')}</span>
        <span className="associations-trigger__count">{panelProps.records.length}</span>
        <KeyboardArrowDownIcon fontSize="small" className="associations-trigger__chevron" />
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { className: 'associations-popover-paper' } }}
      >
        <AssociationsPanelInner {...panelProps} />
      </Popover>
    </div>
  );
}

// Pannello a due colonne. `inline` aggiunge il modifier per il layout a tutta
// larghezza (uso dentro ActivityTools); senza, mantiene la dimensione popover.
export function AssociationsPanelInner({
  records,
  recordsAutocompleteProps,
  recordsListboxProps,
  recordsLoading,
  recordsSaving = false,
  onChange,
  associableTypeNames = [],
  onActiveTypeChange,
  inline = false,
}: AssociationsPanelProps & { inline?: boolean }) {
  const { t } = useTranslation();
  // Filtro tipologia attivo:
  //  - 'selected' → solo i record già selezionati (DEFAULT)
  //  - <typeName> → ricerca/selezione su quel tipo
  const [filter, setFilter] = useState<'selected' | string>('selected');

  // Spinge il tipo attivo al picker per restringere la ricerca: vista
  // "Selezionate" → null (nessuna fetch, filtro client-side); tipo → solo
  // quel tipo. `filter` resta single-source-of-truth, lo scope ne deriva.
  useEffect(() => {
    onActiveTypeChange?.(filter === 'selected' ? null : filter);
  }, [filter, onActiveTypeChange]);

  const selectedByType = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of records) m.set(r.typeName, (m.get(r.typeName) ?? 0) + 1);
    return m;
  }, [records]);

  const types = useMemo(() => {
    if (associableTypeNames.length > 0) return [...associableTypeNames];
    const set = new Set<string>();
    for (const r of records) set.add(r.typeName);
    return [...set];
  }, [associableTypeNames, records]);

  const isSelected = (r: AssociatedRecord) => records.some((s) => sameRecord(s, r));

  function toggle(r: AssociatedRecord) {
    if (isSelected(r)) onChange(records.filter((s) => !sameRecord(s, r)));
    else onChange([...records, r]);
  }

  const rows = useMemo(() => {
    const q = recordsAutocompleteProps.inputValue.trim().toLowerCase();
    if (filter === 'selected') {
      return q ? records.filter((r) => r.label.toLowerCase().includes(q)) : records;
    }
    const map = new Map<string, AssociatedRecord>();
    for (const r of records) if (r.typeName === filter) map.set(recordKey(r), r);
    for (const o of recordsAutocompleteProps.options) if (o.typeName === filter) map.set(recordKey(o), o);
    return [...map.values()];
  }, [records, recordsAutocompleteProps.options, recordsAutocompleteProps.inputValue, filter]);

  const groupedRows = useMemo(() => {
    const groups: { typeName: string; items: AssociatedRecord[] }[] = [];
    const index = new Map<string, number>();
    for (const r of rows) {
      let gi = index.get(r.typeName);
      if (gi === undefined) {
        gi = groups.length;
        index.set(r.typeName, gi);
        groups.push({ typeName: r.typeName, items: [] });
      }
      groups[gi].items.push(r);
    }
    return groups;
  }, [rows]);

  return (
    <div className={`associations-popover${inline ? ' associations-popover--inline' : ''}`}>
      {/* SINISTRA: tipologie con conteggio dei selezionati */}
      <div className="associations-popover__types">
        {/* "Selezionate" è la voce predefinita. Ri-cliccando un tipo attivo
            si torna a "Selezionate". Nessuna voce "Tutte". */}
        <button
          type="button"
          className={`associations-popover__type${filter === 'selected' ? ' is-active' : ''}`}
          onClick={() => setFilter('selected')}
        >
          <span>{t('common.associationsSelected')}</span>
          <span className="associations-popover__type-count">{records.length}</span>
        </button>
        <div className="associations-popover__type-divider" />
        {types.map((tn) => (
          <button
            key={tn}
            type="button"
            className={`associations-popover__type${filter === tn ? ' is-active' : ''}`}
            onClick={() => setFilter((f) => (f === tn ? 'selected' : tn))}
          >
            <span>{typeLabel(tn, t)}</span>
            <span className="associations-popover__type-count">{selectedByType.get(tn) ?? 0}</span>
          </button>
        ))}
      </div>

      {/* DESTRA: ricerca + lista risultati raggruppati */}
      <div className="associations-popover__main">
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder={t('common.search')}
          value={recordsAutocompleteProps.inputValue}
          onChange={(e) => recordsAutocompleteProps.onInputChange(e, e.target.value, 'input')}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <div
          className="associations-popover__results"
          onScroll={(e) => recordsListboxProps.onScroll(e as unknown as UIEvent<HTMLUListElement>)}
        >
          {rows.length === 0 ? (
            <div className="associations-popover__empty text-muted text-sm">
              {recordsLoading ? t('common.loading') : t('common.noResults')}
            </div>
          ) : (
            groupedRows.map((group) => (
              <div key={group.typeName} className="associations-popover__group">
                <div className="associations-popover__group-header">{typeLabel(group.typeName, t)}</div>
                {group.items.map((r) => (
                  <button
                    key={recordKey(r)}
                    type="button"
                    className="associations-popover__row"
                    onClick={() => toggle(r)}
                    disabled={recordsSaving}
                  >
                    <Checkbox size="small" checked={isSelected(r)} tabIndex={-1} disableRipple />
                    <span className="associations-popover__row-label">{r.label}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
